import React from "react";
import { RefreshCw } from "@semo/icons";
import { cn } from "@semo/shared-ui";

interface MessageStatusProps {
  time: string;
  showTime?: boolean;
  isRead: boolean;
  isPending?: boolean;
  isError?: boolean;
  isDeleted?: boolean;
  sender: "me" | "other";
  onRetry?: () => void;
}

export const MessageStatus: React.FC<MessageStatusProps> = ({
  time,
  showTime = true,
  isRead,
  isPending,
  isError,
  isDeleted,
  sender,
  onRetry,
}) => {
  // 말풍선 안쪽(화면 가운데) 방향으로 붙는다.
  // - other(왼쪽 말풍선): 상태는 말풍선 오른쪽에 오므로 왼쪽 정렬로 말풍선에 밀착
  // - me(오른쪽 말풍선): 상태는 말풍선 왼쪽에 오므로 오른쪽 정렬로 말풍선에 밀착
  // min-w 를 두지 않아야 말풍선과 상태 사이에 빈 공간이 생기지 않는다.
  const containerClass = cn(
    "flex flex-col gap-0.5 mb-1 shrink-0 whitespace-nowrap",
    sender === "me" ? "items-end" : "items-start",
  );

  // 1. Error: Show Retry Button
  if (isError && sender === "me" && !isPending) {
    return (
      <div className={containerClass}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRetry?.();
          }}
          className="text-red-500 hover:text-red-600 transition-colors p-1 hover:bg-red-50 rounded-full"
          title="재전송"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // 2. Normal State: Time + Read/Unread Status
  // Prevent flickering: Show '안읽음' immediately when pending
  const displayAsRead = !isPending && isRead;

  return (
    <div className={containerClass}>
      {/* Read/Unread status (본인 메시지만 표시) */}
      {sender === "me" && !isDeleted && (
        <span
          className={cn(
            "text-2xs transition-colors duration-300",
            displayAsRead
              ? "text-slate-400 font-medium"
              : "text-primary-text font-semibold",
          )}
        >
          {displayAsRead ? "읽음" : "안읽음"}
        </span>
      )}
      {showTime && (
        <span className="text-2xs sm:text-2xs text-slate-400">
          {time}
        </span>
      )}
    </div>
  );
};
